import type { CSSProperties, RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChatLayout } from '../types';

interface UseChatScrollOptions {
  keyboardInset?: number;
  layout: ChatLayout;
  topOverlayHeight?: number;
}

interface UseChatScrollResult {
  bottomBarInsetStyle: CSSProperties;
  composerContainerRef: (node: HTMLDivElement | null) => void;
  composerHeight: number;
  getDistanceFromBottom: () => number;
  getScrollContainer: () => HTMLDivElement | null;
  isAtBottom: () => boolean;
  isAtBottomRef: RefObject<boolean>;
  messagesEndRef: RefObject<HTMLDivElement | null>;
  scrollContainerPaddingStyle: CSSProperties;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  scrollToBottom: (behavior?: ScrollBehavior) => void;
  updateAtBottomFlag: () => void;
}

const BOTTOM_OFFSET = 24;

export const useChatScroll = ({
  layout,
  topOverlayHeight = 0,
  keyboardInset = 0,
}: UseChatScrollOptions): UseChatScrollResult => {
  const scrollContainerReference = useRef<HTMLDivElement | null>(null);
  const messagesEndReference = useRef<HTMLDivElement | null>(null);
  const [composerElement, setComposerElement] = useState<HTMLDivElement | null>(null);
  const isAtBottomReference = useRef(true);
  const [composerHeight, setComposerHeight] = useState(0);
  const previousComposerHeightReference = useRef(0);
  const previousOverlayHeightReference = useRef(topOverlayHeight);

  const updateAtBottomFlag = useCallback(() => {
    const container = scrollContainerReference.current;
    if (!container) {
      isAtBottomReference.current = true;
      return;
    }
    const distanceFromBottom = container.scrollHeight - container.clientHeight - container.scrollTop;
    isAtBottomReference.current = distanceFromBottom <= BOTTOM_OFFSET;
  }, []);

  const getDistanceFromBottom = useCallback(() => {
    const container = scrollContainerReference.current;
    if (!container) {
      return 0;
    }
    return container.scrollHeight - container.clientHeight - container.scrollTop;
  }, []);

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      const container = scrollContainerReference.current;
      if (container) {
        container.scrollTo({ top: container.scrollHeight, behavior });
        if (globalThis.window !== undefined && typeof globalThis.requestAnimationFrame === 'function') {
          globalThis.requestAnimationFrame(updateAtBottomFlag);
        } else {
          updateAtBottomFlag();
        }
        return;
      }
      messagesEndReference.current?.scrollIntoView({ behavior, block: 'end' });
      isAtBottomReference.current = true;
    },
    [updateAtBottomFlag]
  );

  useEffect(() => {
    const element = composerElement;
    if (!element || globalThis.window === undefined || !globalThis.ResizeObserver) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const box = entry.contentRect;
        setComposerHeight(Math.round(box.height));
      }
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [composerElement]);

  useEffect(() => {
    const container = scrollContainerReference.current;
    if (!container) {
      return;
    }
    const handleScroll = () => {
      updateAtBottomFlag();
    };
    handleScroll();
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [updateAtBottomFlag]);

  const keyboardInsetPx = `${Math.max(0, Math.round(keyboardInset))}px`;

  const bottomBarInsetStyle: CSSProperties = useMemo(
    () => ({
      paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${keyboardInsetPx} + 1rem)`,
      paddingLeft: 'env(safe-area-inset-left, 0px)',
      paddingRight: 'env(safe-area-inset-right, 0px)',
    }),
    [keyboardInsetPx]
  );

  const scrollContainerPaddingStyle: CSSProperties = useMemo(() => {
    // Ensure the sidebar layout never hugs the top edge and keeps the composer within reach.
    const overlayTopPadding = Math.max(40, Math.round(topOverlayHeight) + 48);
    if (layout === 'sidebar') {
      const computedBottomPadding = composerHeight > 0 ? composerHeight + 40 : 168;
      return {
        paddingTop: `${overlayTopPadding}px`,
        scrollPaddingTop: `${overlayTopPadding}px`,
        paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${keyboardInsetPx} + ${computedBottomPadding}px)`,
      } satisfies CSSProperties;
    }
    const computedBottomPadding = composerHeight > 0 ? composerHeight + 72 : 240;
    const topPadding = topOverlayHeight > 0 ? Math.max(16, Math.round(topOverlayHeight) + 12) : 0;
    return {
      paddingTop: topPadding > 0 ? `${topPadding}px` : undefined,
      paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${keyboardInsetPx} + ${computedBottomPadding}px)`,
    } satisfies CSSProperties;
  }, [composerHeight, keyboardInsetPx, layout, topOverlayHeight]);

  useEffect(() => {
    if (!Number.isFinite(keyboardInset)) {
      updateAtBottomFlag();
      return;
    }
    updateAtBottomFlag();
  }, [keyboardInset, updateAtBottomFlag]);

  useEffect(() => {
    const container = scrollContainerReference.current;
    if (!container) {
      previousComposerHeightReference.current = composerHeight;
      return;
    }
    const previousHeight = previousComposerHeightReference.current;
    previousComposerHeightReference.current = composerHeight;
    if (!isAtBottomReference.current) {
      return;
    }
    if (Math.abs(composerHeight - previousHeight) < 1) {
      return;
    }
    container.scrollTop = container.scrollHeight - container.clientHeight;
    updateAtBottomFlag();
  }, [composerHeight, updateAtBottomFlag]);

  useEffect(() => {
    const container = scrollContainerReference.current;
    if (!container) {
      previousOverlayHeightReference.current = topOverlayHeight;
      return;
    }
    const previousHeight = previousOverlayHeightReference.current;
    previousOverlayHeightReference.current = topOverlayHeight;
    if (!isAtBottomReference.current) {
      return;
    }
    if (Math.abs(topOverlayHeight - previousHeight) < 1) {
      return;
    }
    container.scrollTop = container.scrollHeight - container.clientHeight;
    updateAtBottomFlag();
  }, [topOverlayHeight, updateAtBottomFlag]);

  const handleComposerReference = useCallback((node: HTMLDivElement | null) => {
    setComposerElement(node);
  }, []);

  const getScrollContainer = useCallback(() => scrollContainerReference.current, []);
  const isAtBottom = useCallback(() => isAtBottomReference.current, []);

  return {
    scrollContainerRef: scrollContainerReference,
    messagesEndRef: messagesEndReference,
    composerContainerRef: handleComposerReference,
    isAtBottomRef: isAtBottomReference,
    scrollContainerPaddingStyle,
    bottomBarInsetStyle,
    scrollToBottom,
    updateAtBottomFlag,
    composerHeight,
    getDistanceFromBottom,
    getScrollContainer,
    isAtBottom,
  };
};
